// @flow
import React from 'react';

import Drawer from 'material-ui/Drawer';
import Toolbar, { ToolbarTitle } from 'material-ui/Toolbar';
import Subheader from 'material-ui/Subheader';

import { GridList, GridTile } from 'material-ui/GridList';
import IconButton from 'material-ui/IconButton';
import FontIcon from 'material-ui/FontIcon';

import { List, ListItem } from 'material-ui/List';
import Toggle from 'material-ui/Toggle';

import { cyan100, cyan500 } from 'material-ui/styles/colors';

import FeatureToggle from 'components/FeatureToggle';
import mapLayerIcon from './MapLayerIcon';
import { STYLE_CATEGORIES } from 'utils/mapStyle';
import { FEATURE_STYLE_MAPPING, isFeatureDisabled } from '../../utils/auth';

const styles = {
  drawer: {
    paddingTop: 72,
    overflowX: 'hidden',
  },

  topmostSubheader: {
    padding: '20px 0 20px 16px',
  },

  toolbar: {
    background: cyan100,
  },

  toolbarTitle: {
    color: cyan500,
    fontSize: '.85em',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginTop: '.2em',
  },

  gridTile: {
    cursor: 'pointer',
  },

  featureTypeItem: {
    fontSize: 'smaller',
    padding: '10px 72px',
    lineHeight: '25px',
  },

  disabledListItem: {
    background: '#dedbdb',
    cursor: 'not-allowed',
    opacity: 0.5,
  },
};

const radioButton = ({ selected, onClick }) => (
  <IconButton
    key="back"
    iconClassName="material-icons"
    iconStyle={styles.iconButton}
    onClick={onClick}
    iconStyle={{ color: 'white' }}
  >
    {selected ? 'radio_button_checked' : 'radio_button_unchecked'}
  </IconButton>
);

const AirspaceTypeToggle = ({ style, toggled, onToggle, styleCategory, disabled }) => (
  <ListItem
    style={style}
    primaryText={styleCategory.label}
    rightToggle={<Toggle toggled={toggled} onToggle={onToggle} disabled={disabled}/>}
    leftAvatar={mapLayerIcon({
      ...styleCategory.style,
      color: styleCategory.color,
    })}
  />
);

export default ({
  mapLayerDrawer: { open },

  browser: {
    lessThan: { medium: isMobile },
  },

  map,

  setBackground,
  toggleExtrusion,
  toggleStyleCategory,
  toggleAirports,
  toggleAircrafts,
  goToCurrentLocation,
  permission,
  hasSubscription,
}) => {
  const isDenied = permission === 'denied';
  const secondaryText = isDenied ? (
    <p>A pozícióhoz való hozzáférés nem engedélyezett.</p>
  ) : (
    undefined
  );
  const mapGeographicalDisabled = isFeatureDisabled('map_geographical');
  const mapSatelliteDisabled = isFeatureDisabled('map_satellite');
  const aircraftsDisabled = isFeatureDisabled('aircrafts');
  const view3dDisabled= isFeatureDisabled('view_3d');
  const airportsDisabled = isFeatureDisabled('airports');
  const trackYourPositionDisabled = isFeatureDisabled('track_your_position');
  if (mapGeographicalDisabled && !mapSatelliteDisabled) {
    setBackground('satellite')
  }

  return (
    <Drawer
      open={open}
      openSecondary
      disableSwipeToOpen
      containerStyle={styles.drawer}
      width={isMobile ? '85%' : 400}
    >
      <Subheader style={styles.topmostSubheader}>Háttér</Subheader>
      <GridList cellHeight={100}>
        <GridTile
          key="bg-default"
          title="Földrajzi"
          style={mapGeographicalDisabled ? styles.disabledListItem : styles.gridTile}
          onClick={() => {if (!mapGeographicalDisabled) setBackground('osm')}}
          actionIcon={mapGeographicalDisabled ? null : radioButton({
            selected: map.background === 'osm',
            onClick: () => setBackground('osm'),
            disabled: {mapGeographicalDisabled},
          })}
        >
          <img src="/assets/layers/bg-default.jpg" />
        </GridTile>
        <GridTile
          key="bg-sat"
          title="Műhold"
          style={mapSatelliteDisabled ? styles.disabledListItem : styles.gridTile}
          onClick={() => {if (!mapSatelliteDisabled) setBackground('satellite')}}
          actionIcon={mapSatelliteDisabled ? null : radioButton({
            selected: map.background === 'satellite',
            onClick: () => setBackground('satellite'),
            disabled: {mapSatelliteDisabled},
          })}
        >
          <img src="/assets/layers/bg-sat.jpg" />
        </GridTile>
      </GridList>

      <Subheader>Légterek</Subheader>
      <List>
        {Object.keys(STYLE_CATEGORIES)
          .filter(k => STYLE_CATEGORIES[k].showInLayerDrawer)
          .map(k => {
            const styleCategory = STYLE_CATEGORIES[k];
            const feature = FEATURE_STYLE_MAPPING[k];
            const disabled = isFeatureDisabled(feature);
            const toggled = disabled ? false : map.styleCategories.includes(k);

            const airspaceTypeToggle = (
              <AirspaceTypeToggle
                key={`style-category-toggle-${k}`}
                style={styles.featureTypeItem}
                toggled={toggled}
                onToggle={() => toggleStyleCategory(k)}
                styleCategory={styleCategory}
                disabled={disabled}
              />
            );

            return styleCategory.featureFlag ? (
              <FeatureToggle
                flag={styleCategory.featureFlag}
                key={`style-category-toggle-${k}`}
               >
                 {airspaceTypeToggle}
               </FeatureToggle>
             ) : (
               airspaceTypeToggle
             );
          })}
      </List>

      <Subheader>Egyéb</Subheader>
      <List>
        {false && (
        <ListItem
          hidden={true}
          leftIcon={<FontIcon className="material-icons">flight</FontIcon>}
          primaryText="Légijárművek"
          rightToggle={
            <Toggle
              toggled={aircraftsDisabled ? false : map.showAircrafts}
              onToggle={() => toggleAircrafts(!map.showAircrafts)}
              disabled={aircraftsDisabled}
            />
          }
        />
        )}
        <ListItem
          leftIcon={<FontIcon className="material-icons">3d_rotation</FontIcon>}
          primaryText="3D nézet"
          rightToggle={
            <Toggle
              toggled={view3dDisabled ? false : map.showExtrusion}
              onToggle={() => toggleExtrusion(!map.showExtrusion)}
              disabled={view3dDisabled}
            />
          }
        />
        <ListItem
          leftIcon={<FontIcon className="material-icons">flight_takeoff</FontIcon>}
          primaryText="Repülőterek"
          rightToggle={
            <Toggle
              toggled={airportsDisabled ? false : map.showAirports}
              onToggle={() => toggleAirports(!map.showAirports)}
              disabled={airportsDisabled}
            />
          }
        />
        {hasSubscription && (
          <ListItem
            leftIcon={<FontIcon className="material-icons">gps_not_fixed</FontIcon>}
            primaryText="Saját pozíció követése"
            secondaryText={secondaryText}
            disabled={trackYourPositionDisabled}
            style={trackYourPositionDisabled ? styles.disabledListItem : undefined}
            onClick={() => {
              goToCurrentLocation();
            }}
          />
        )}
      </List>
    </Drawer>
  );
};



// WEBPACK FOOTER //
// ./components/MapLayerDrawer/MapLayerDrawer.js